import React, { useState, useMemo, useEffect } from 'react';

/**
 * ROI Calculator for Sales Prospects
 *
 * A beautiful interactive form to calculate potential ROI from Ankr VCX capabilities
 */
const ROICalculator = () => {
  // Set page title
  useEffect(() => {
    document.title = 'ROI Calculator for AI Employees | Ankr Health';
    return () => {
      document.title = 'Dashboard | Ankr Health - AI Workforce for Medical Practices'; // Reset on unmount
    };
  }, []);

  const [selectedCapabilities, setSelectedCapabilities] = useState({
    appointments: false,
    medications: false,
    medicalIssues: false,
    billingInsurance: false,
    medicalRecords: false,
    generalInquiries: false,
    providerCalls: false,
    vendorCalls: false
  });

  const [callVolumes, setCallVolumes] = useState({
    appointments: 50,
    medications: 50,
    medicalIssues: 50,
    billingInsurance: 50,
    medicalRecords: 50,
    generalInquiries: 50,
    providerCalls: 50,
    vendorCalls: 50
  });

  const [detailedCapabilities, setDetailedCapabilities] = useState({
    appointments: {
      provideInfo: false,
      reschedule: false,
      simpleReschedule: false,
      complexReschedule: false,
      cancel: false
    },
    medications: {
      provideInfo: false,
      collectRefillInfo: false,
      sendNote: false,
      callPharmacy: false,
      updateEHR: false
    },
    medicalIssues: {
      collectInfo: false,
      lookupNotes: false,
      createNote: false,
      sendTeamsMessage: false,
      sendEHRMessage: false,
      followGuidelines: false
    },
    billingInsurance: {
      provideInfo: false,
      updateInsurance: false,
      payBills: false,
      getEstimates: false
    },
    medicalRecords: {
      submitRequest: false,
      getStatus: false,
      signUpPortal: false
    },
    generalInquiries: {
      hoursDirections: false,
      urgentNotifications: false,
      otherInquiries: false
    },
    providerCalls: {
      doctorOffice: false,
      emergencyRooms: false,
      otherFacilities: false
    },
    vendorCalls: {
      approvedVendors: false,
      unknownVendors: false,
      otherCallers: false
    }
  });

  const [showResults, setShowResults] = useState(false);

  // Manual FTE overrides (null means use calculated value)
  const [manualMiraFTEs, setManualMiraFTEs] = useState(null);
  const [manualFrankFTEs, setManualFrankFTEs] = useState(null);

  // Enhanced ROI calculations across 3 domains: Revenue, Margins/Cost Savings, Patient Access
  const calculations = useMemo(() => {
    const avgCallDuration = 5; // minutes
    const avgStaffHourlyRate = 25; // dollars (including benefits)
    const ankrSavingsRate = 0.70; // 70% time savings
    const daysPerMonth = 22; // working days

    // Count selected detailed capabilities for bonus multiplier
    let selectedDetailsCount = 0;
    Object.keys(detailedCapabilities).forEach(capKey => {
      Object.keys(detailedCapabilities[capKey]).forEach(detailKey => {
        if (detailedCapabilities[capKey][detailKey]) {
          selectedDetailsCount++;
        }
      });
    });
    // Apply 6-10% improvement per selected capability detail
    const improvementPerDetail = 0.06 + (Math.random() * 0.04); // 6-10%
    const capabilityMultiplier = Math.pow(1 + improvementPerDetail, selectedDetailsCount);

    let totalCalls = 0;
    let monthlySavingsMinutes = 0;

    Object.keys(selectedCapabilities).forEach(key => {
      if (selectedCapabilities[key]) {
        const dailyCalls = callVolumes[key] || 0;
        totalCalls += dailyCalls;
        monthlySavingsMinutes += dailyCalls * avgCallDuration * ankrSavingsRate * daysPerMonth;
      }
    });

    const monthlySavingsHours = (monthlySavingsMinutes / 60) * capabilityMultiplier;
    const monthlySavingsDollars = monthlySavingsHours * avgStaffHourlyRate;
    const annualSavingsDollars = monthlySavingsDollars * 12;

    // Calculate Mira and Frank costs
    const fteCostPerHour = 10.5;
    const fteCostPerWeek = 40 * fteCostPerHour;
    const fteCostPer2Weeks = 40 * 2 * fteCostPerHour;
    const fteCostPerMonth = 1820; // $10.5/hour x 40 hours/week
    const fteCostPerYear = 21840;

    // Mira FTEs: 1 FTE per 100 calls/day (use manual override if available)
    const calculatedMiraFTEs = Math.ceil(totalCalls / 100);
    const miraFTEs = manualMiraFTEs !== null ? manualMiraFTEs : calculatedMiraFTEs;

    // Frank FTEs: For appointments, medical issues, and billing/insurance (100 calls each)
    const frankCallsPerDay = (selectedCapabilities.appointments ? (callVolumes.appointments || 0) : 0) +
                              (selectedCapabilities.medicalIssues ? (callVolumes.medicalIssues || 0) : 0) +
                              (selectedCapabilities.billingInsurance ? (callVolumes.billingInsurance || 0) : 0);
    const calculatedFrankFTEs = Math.ceil(frankCallsPerDay / 100);
    const frankFTEs = manualFrankFTEs !== null ? manualFrankFTEs : calculatedFrankFTEs;

    const totalFTEs = miraFTEs + frankFTEs;
    const totalWeeklyCost = totalFTEs * fteCostPerWeek;
    const totalBiWeeklyCost = totalFTEs * fteCostPer2Weeks;
    const totalMonthlyCost = totalFTEs * fteCostPerMonth;
    const totalAnnualCost = totalFTEs * fteCostPerYear;

    // Revenue Impact Calculations (with capability multiplier)
    const appointmentCalls = selectedCapabilities.appointments ? (callVolumes.appointments || 0) : 0;
    const appointmentConversionRate = 0.05; // 5% of appointment calls convert to scheduled appointments
    const appointmentShowRate = 0.60; // 60% show up
    const avgRevenuePerVisit = 150; // Average revenue per patient visit
    const newAppointmentsPerMonth = appointmentCalls * daysPerMonth * appointmentConversionRate * appointmentShowRate * capabilityMultiplier;
    const monthlyRevenueIncrease = newAppointmentsPerMonth * avgRevenuePerVisit;
    const annualRevenueIncrease = monthlyRevenueIncrease * 12;

    // Patient Access Improvements (with capability multiplier)
    const avgWaitTimeReduction = Math.min(75 * capabilityMultiplier, 95); // 75% reduction in phone wait time, capped at 95%
    const callsAnsweredRate = Math.min(0.95 * capabilityMultiplier, 0.99); // 95% of calls answered (vs typical 60-70%), capped at 99%
    const missedCallRecovery = totalCalls * daysPerMonth * 0.30 * capabilityMultiplier; // Recover 30% of previously missed calls
    const patientSatisfactionImprovement = Math.min(40 * capabilityMultiplier, 60); // 40% improvement in patient satisfaction scores, capped at 60%

    return {
      // Basic metrics
      totalDailyCalls: totalCalls,
      totalMonthlyCalls: totalCalls * daysPerMonth,
      monthlySavingsHours: monthlySavingsHours.toFixed(1),
      monthlySavingsDollars: monthlySavingsDollars.toFixed(0),
      annualSavingsDollars: annualSavingsDollars.toFixed(0),

      // Revenue Domain
      newAppointmentsPerMonth: Math.round(newAppointmentsPerMonth),
      monthlyRevenueIncrease: Math.round(monthlyRevenueIncrease),
      annualRevenueIncrease: Math.round(annualRevenueIncrease),

      // Margins/Cost Savings Domain
      staffHoursSaved: monthlySavingsHours.toFixed(1),
      monthlyLaborCostSavings: monthlySavingsDollars.toFixed(0),
      annualLaborCostSavings: annualSavingsDollars.toFixed(0),
      fteReduction: (monthlySavingsHours / 160).toFixed(1), // Hours saved / standard monthly hours

      // Patient Access Domain
      waitTimeReduction: avgWaitTimeReduction.toFixed(1),
      callAnswerRate: (callsAnsweredRate * 100).toFixed(0),
      additionalPatientsServed: Math.round(missedCallRecovery),
      patientSatisfactionGain: patientSatisfactionImprovement.toFixed(1),

      // Ankr Cost (Mira & Frank)
      miraFTEs,
      frankFTEs,
      totalFTEs,
      totalWeeklyCost: totalWeeklyCost.toFixed(0),
      totalBiWeeklyCost: totalBiWeeklyCost.toFixed(0),
      totalMonthlyCost: totalMonthlyCost.toFixed(0),
      totalAnnualCost: totalAnnualCost.toFixed(0),

      // Net Impact (after Ankr costs)
      netMonthlySavings: (monthlySavingsDollars + monthlyRevenueIncrease - totalMonthlyCost).toFixed(0),
      netAnnualSavings: (annualSavingsDollars + annualRevenueIncrease - totalAnnualCost).toFixed(0)
    };
  }, [selectedCapabilities, callVolumes, detailedCapabilities, manualMiraFTEs, manualFrankFTEs]);

  const capabilities = [
    {
      id: 'appointments',
      icon: '📅',
      title: 'Appointments',
      description: 'Schedule, reschedule, and manage appointments',
      details: [
        { id: 'provideInfo', label: 'Provide appointment information from EHR' },
        { id: 'reschedule', label: 'Reschedule Appointment' },
        { id: 'simpleReschedule', label: 'Simple Rescheduling (office visit with same provider)' },
        { id: 'complexReschedule', label: 'Complex Rescheduling (Procedures, new patients etc)' },
        { id: 'cancel', label: 'Cancel Appointment' }
      ]
    },
    {
      id: 'medications',
      icon: '💊',
      title: 'Medications / Pharmacy',
      description: 'Handle prescription refills and pharmacy questions',
      details: [
        { id: 'provideInfo', label: 'Provide prescription information from EHR' },
        { id: 'collectRefillInfo', label: 'Collect detailed information for prescription refills' },
        { id: 'sendNote', label: 'Send phone encounter note to staff in EHR' },
        { id: 'callPharmacy', label: 'Call Walgreens/CVS to get refill status' },
        { id: 'updateEHR', label: 'Update medications in EHR' }
      ]
    },
    {
      id: 'medicalIssues',
      icon: '🏥',
      title: 'Medical Issues (Triage Assist)',
      description: 'Collect detailed information for medical triage',
      note: 'Mira is not a licensed professional and can NOT make medical decisions. However, it can collect really detailed information and cut time to response for urgent medical issues by 50-70%',
      details: [
        { id: 'collectInfo', label: 'Collect detailed information for medical issue' },
        { id: 'lookupNotes', label: 'Look up clinic notes in EHR during the call' },
        { id: 'createNote', label: 'Create phone encounter note in EHR' },
        { id: 'sendTeamsMessage', label: 'Send Microsoft Teams or email message to Triage Nursing Pool' },
        { id: 'sendEHRMessage', label: 'Send EHR message to Triage Nursing Pool' },
        { id: 'followGuidelines', label: 'Follow national nurse triage (STCC) guidelines' }
      ]
    },
    {
      id: 'billingInsurance',
      icon: '💳',
      title: 'Billing / Insurance',
      description: 'Handle billing questions and insurance updates',
      details: [
        { id: 'provideInfo', label: 'Information about Insurance & Outstanding Bills' },
        { id: 'updateInsurance', label: 'Update Insurance' },
        { id: 'payBills', label: 'Pay Outstanding Bills' },
        { id: 'getEstimates', label: 'Get Estimates ("No Surprises Act")' }
      ]
    },
    {
      id: 'medicalRecords',
      icon: '📋',
      title: 'Medical Records',
      description: 'Manage medical records requests',
      details: [
        { id: 'submitRequest', label: 'Submit Medical Records Request' },
        { id: 'getStatus', label: 'Get Status Update on Existing Request' },
        { id: 'signUpPortal', label: 'Sign Up for Patient Portal' }
      ]
    },
    {
      id: 'generalInquiries',
      icon: 'ℹ️',
      title: 'General Inquiries',
      description: 'Answer general clinic questions',
      details: [
        { id: 'hoursDirections', label: 'Clinic Hours/Directions' },
        { id: 'urgentNotifications', label: 'Urgent Notifications (e.g. changes due to storms/hurricanes)' },
        { id: 'otherInquiries', label: 'Other General Inquiries' }
      ]
    },
    {
      id: 'providerCalls',
      icon: '👨‍⚕️',
      title: 'Healthcare Provider Calls',
      description: 'Handle calls from other healthcare providers',
      details: [
        { id: 'doctorOffice', label: "From Other Doctor/Doctor's Office" },
        { id: 'emergencyRooms', label: 'From Emergency Rooms/Urgent Care/Hospitals' },
        { id: 'otherFacilities', label: 'Other Healthcare Facilities' }
      ]
    },
    {
      id: 'vendorCalls',
      icon: '📞',
      title: 'Vendors, Other Calls',
      description: 'Screen vendor and unsolicited calls',
      details: [
        { id: 'approvedVendors', label: 'Approved Vendors' },
        { id: 'unknownVendors', label: 'Unknown Vendors' },
        { id: 'otherCallers', label: 'Other Callers (e.g. Lawyers)' }
      ]
    }
  ];

  const handleCapabilityToggle = (capabilityId) => {
    setSelectedCapabilities(prev => ({
      ...prev,
      [capabilityId]: !prev[capabilityId]
    }));
  };

  const handleVolumeChange = (capabilityId, value) => {
    setCallVolumes(prev => ({
      ...prev,
      [capabilityId]: parseInt(value) || 0
    }));
  };

  const handleDetailToggle = (capabilityId, detailId) => {
    setDetailedCapabilities(prev => ({
      ...prev,
      [capabilityId]: {
        ...prev[capabilityId],
        [detailId]: !prev[capabilityId][detailId]
      }
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedCapabilities).every(v => v);
    const newState = {};
    Object.keys(selectedCapabilities).forEach(key => {
      newState[key] = !allSelected;
    });
    setSelectedCapabilities(newState);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Reset manual overrides when recalculating
    setManualMiraFTEs(null);
    setManualFrankFTEs(null);
    setShowResults(true);
    // Scroll to results
    setTimeout(() => {
      document.getElementById('roi-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleMiraFTEChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setManualMiraFTEs(numValue);
    }
  };

  const handleFrankFTEChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setManualFrankFTEs(numValue);
    }
  };

  return (
    <div className="roi-calculator-page min-vh-100">
      <div className="container">
        {/* Header */}
        <div className="row justify-content-center mb-4">
          <div className="col-lg-10">
            <div className="card roi-header-card shadow-lg border-0">
              <div className="card-body text-center p-5">
                <div className="d-flex flex-column align-items-center">
                  <img src="https://framerusercontent.com/images/6NVV3lXAy7GrYIQNuxrzT7fww.svg?width=150" width='150px' height='auto' alt='Ankr VCX' className="mb-3" />
                  <h2 className="display-4 fw-bold mb-3 text-primary">Return on Investment (ROI) Calculator</h2>
                  <p className="lead mb-2">
                    What would you like your new AI employees – Mira, Frank and Sol – to do?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Select All Button */}
          <div className="row justify-content-center mb-4">
            <div className="col-lg-10 text-center">
                <p className="text-muted pb-3">
                  Don&apos;t worry, there are no wrong choices with Ankr... you&apos;ll have complete control
                  on the settings of each of these capabilities once you finish onboarding.
                </p>
              <button
                type="button"
                className="btn text-white roi-submit-btn btn-lg px-5"
                onClick={handleSelectAll}
              >
                {Object.values(selectedCapabilities).every(v => v) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>

          {/* Capabilities Grid */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row g-4 mb-4">
                {capabilities.map(capability => (
                  <div key={capability.id} className="col-md-6">
                    <div className={`card shadow-sm ${selectedCapabilities[capability.id] ? 'border-primary border-2 capability-card-selected' : ''}`}>
                      <div className="card-body">
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="d-flex gap-3 flex-grow-1">
                            <div className="capability-icon-wrapper">
                              <span className="fs-1">{capability.icon}</span>
                            </div>
                            <div>
                              <h5 className="card-title fw-bold mb-1">{capability.title}</h5>
                              <p className="card-text text-muted mb-0">{capability.description}</p>
                            </div>
                          </div>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input capability-toggle"
                              type="checkbox"
                              role="switch"
                              checked={selectedCapabilities[capability.id]}
                              onChange={() => handleCapabilityToggle(capability.id)}
                            />
                          </div>
                        </div>

                        {/* Note */}
                        {capability.note && selectedCapabilities[capability.id] && (
                          <div className="alert alert-warning d-flex gap-2 mb-3">
                            <span>ℹ️</span>
                            <div>{capability.note}</div>
                          </div>
                        )}

                        {/* Details */}
                        {selectedCapabilities[capability.id] && (
                          <div className="border-top pt-3">
                            {/* Volume Input */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Daily Call Volume</label>
                              <input
                                type="number"
                                className="form-control"
                                value={callVolumes[capability.id]}
                                onChange={(e) => handleVolumeChange(capability.id, e.target.value)}
                                min="0"
                                max="10000"
                              />
                            </div>

                            {/* Detailed Options */}
                            <div className="rounded p-3">
                              <h6 className="text-uppercase fw-bold text-muted mb-2">
                                Specific Capabilities:
                              </h6>
                              {capability.details.map(detail => (
                                <div key={detail.id} className="form-check mb-2">
                                  <input
                                    className="form-check-input text-lg"
                                    type="checkbox"
                                    id={`${capability.id}-${detail.id}`}
                                    checked={detailedCapabilities[capability.id][detail.id]}
                                    onChange={() => handleDetailToggle(capability.id, detail.id)}
                                  />
                                  <label
                                    className="form-check-label text-lg"
                                    htmlFor={`${capability.id}-${detail.id}`}
                                  >
                                    {detail.label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow border-0">
              {/* Total Calls Summary */}
              {calculations.totalDailyCalls > 0 && (
                <div className="text-center mt-3">
                  <p className="mb-1 text-muted">
                    <strong>Total Daily Calls:</strong> <span className="text-primary fw-bold fs-4">{calculations.totalDailyCalls}</span>
                  </p>
                </div>
              )}

                <div className="card-body text-center p-4">
                  <button type="submit" className="btn btn-primary btn-lg px-5 mb-3 roi-submit-btn">
                    Analyze ROI
                  </button>
                  <p className="text-muted mb-0">
                    Get a detailed breakdown of your potential savings and implementation plan
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* ROI Results - Three Domains */}
        
        {showResults && (
          <div id="roi-results" className="row justify-content-center mt-5">
            <div className="col-lg-10">
              <div className="card shadow-lg border-0 mb-4">
                <div className="card-body p-5">
                  <h2 className="text-center mb-4 fw-bold">Your Comprehensive ROI Analysis</h2>
                  <p className="text-center text-muted mb-5">
                    Based on your selections, here&apos;s how Ankr VCX will impact your practice across three critical domains
                  </p>
{/* Ankr Cost Breakdown */}
                  <div className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="domain-icon me-3">💵</div>
                      <h3 className="mb-0 fw-bold text-primary">Ankr Investment</h3>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <div className="card bg-primary bg-opacity-10 border-primary border-2 ankr-investment-card">
                          <div className="card-body text-center">
                            <img
                              src="/dist/assets/images/mira_ankr.jpg" alt="Mira Ankr Headshot"
                              className="rounded-circle mb-2 d-block mx-auto"
                              style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                            />
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <button
                                className="btn text-lg"
                                onClick={() => handleMiraFTEChange(Math.max(0, calculations.miraFTEs - 1))}
                                type="button"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center fw-bold"
                                style={{ width: '80px', fontSize: '1.5rem' }}
                                value={calculations.miraFTEs}
                                onChange={(e) => handleMiraFTEChange(e.target.value)}
                                min="0"
                              />
                              <button
                                className="btn text-lg"
                                onClick={() => handleMiraFTEChange(calculations.miraFTEs + 1)}
                                type="button"
                              >
                                +
                              </button>
                            </div>
                            <p className="text-lg mt-2">Mira FTEs</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-primary bg-opacity-10 border-primary border-2 ankr-investment-card">
                          <div className="card-body text-center">
                            <img
                              src="/dist/assets/images/frank_ankr.jpg" alt="Frank Ankr Headshot"
                              className="rounded-circle mb-2 d-block mx-auto"
                              style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                            />
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <button
                                className="btn text-lg"
                                onClick={() => handleFrankFTEChange(Math.max(0, calculations.frankFTEs - 1))}
                                type="button"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center fw-bold"
                                style={{ width: '80px', fontSize: '1.5rem' }}
                                value={calculations.frankFTEs}
                                onChange={(e) => handleFrankFTEChange(e.target.value)}
                                min="0"
                              />
                              <button
                                className="btn text-lg"
                                onClick={() => handleFrankFTEChange(calculations.frankFTEs + 1)}
                                type="button"
                              >
                                +
                              </button>
                            </div>
                            <p className="text-lg mt-2">Frank FTEs</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-primary bg-opacity-10 border-primary border-2 ankr-investment-card">
                          <div className="card-body text-center">
                            <img
                              src="/dist/assets/images/sol_ankr.jpg" alt="Sol Ankr Headshot"
                              className="rounded-circle mb-2 d-block mx-auto"
                              style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                            />
                            <input
                                type="text"
                                className="form-control form-control-sm text-center fw-bold"
                                style={{ fontSize: '1.5rem' }}
                                placeholder='Included'
                              />
                            <p className="mt-2">Sol FTEs come bundled for free!</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-primary bg-opacity-10 border-primary border-2 ankr-investment-card">
                          <div className="card-body text-center">
                            <div className="h2 fw-bold text-primary">${calculations.totalBiWeeklyCost.toLocaleString()}</div>
                            <p className="mb-0">BiWeekly Ankr Payroll</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-muted mt-3 mb-0">
                      <strong>How we calculate this:</strong> {calculations.miraFTEs} Mira FTE{calculations.miraFTEs !== 1 ? 's' : ''} (1 per 100 calls/day)
                      + {calculations.frankFTEs} Frank FTE{calculations.frankFTEs !== 1 ? 's' : ''} (for appointments, medical issues, and billing support)
                      at $1,820/month per FTE. <em>Adjust FTEs above to see updated costs.</em>
                    </p>
                  </div>

                  {/* Domain 1: Revenue Growth */}
                  <div className="mb-5">
                    <div className="d-flex align-items-center mb-3">
                      <div className="domain-icon me-3">💰</div>
                      <h3 className="mb-0 fw-bold text-success">Revenue Growth</h3>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="card bg-success bg-opacity-10 border-success border-2 h-100">
                          <div className="card-body text-center">
                            <div className="h2 fw-bold text-success">{calculations.newAppointmentsPerMonth}</div>
                            <p className="text-muted">New Appointments/Month</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-success bg-opacity-10 border-success border-2 h-100">
                          <div className="card-body text-center">
                            <div className="h2 fw-bold text-success">${calculations.monthlyRevenueIncrease.toLocaleString()}</div>
                            <p className="text-muted">Monthly Revenue Increase</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-success bg-opacity-10 border-success border-2 h-100">
                          <div className="card-body text-center">
                            <div className="h2 fw-bold text-success">${calculations.annualRevenueIncrease.toLocaleString()}</div>
                            <p className="text-muted">Annual Revenue Increase</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-muted mt-3 mb-0">
                      <strong>How we calculate this:</strong> 5% of appointment calls convert to scheduled appointments,
                      with a 60% show rate and average $150 revenue per visit.
                    </p>
                  </div>

                  {/* Domain 2: Margins & Cost Savings */}
                  <div className="mb-5">
                    <div className="d-flex align-items-center mb-3">
                      <div className="domain-icon me-3">📊</div>
                      <h3 className="mb-0 fw-bold text-primary">Margins & Cost Savings</h3>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <div className="card roi-highlight-card h-100">
                          <div className="card-body text-center text-white">
                            <div className="h2 fw-bold">{calculations.staffHoursSaved}</div>
                            <p>Hours Saved/Month</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card roi-highlight-card h-100">
                          <div className="card-body text-center text-white">
                            <div className="h2 fw-bold">{calculations.fteReduction}</div>
                            <p>FTE Reduction</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card roi-highlight-card h-100">
                          <div className="card-body text-center text-white">
                            <div className="h2 fw-bold">${calculations.monthlyLaborCostSavings.toLocaleString()}</div>
                            <p>Monthly Labor Savings</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card roi-highlight-card h-100">
                          <div className="card-body text-center text-white">
                            <div className="h2 fw-bold">${calculations.annualLaborCostSavings.toLocaleString()}</div>
                            <p>Annual Labor Savings</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-muted mt-3 mb-0">
                      <strong>How we calculate this:</strong> Ankr handles calls 70% faster than staff, saving time that
                      would otherwise cost $25/hour in labor (including benefits). Beyond direct cost savings, Ankr eliminates
                      the challenges of finding qualified staff, 2-3 month training periods, and 50% annual attrition rates.
                    </p>
                  </div>

                  {/* Domain 3: Patient Access */}
                  <div className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="domain-icon me-3">🏥</div>
                      <h3 className="mb-0 fw-bold text-info">Patient Access & Satisfaction</h3>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <div className="card bg-info bg-opacity-10 border-info border-2 h-100">
                          <div className="card-body text-center">
                            <div className="h2 fw-bold text-info">{calculations.waitTimeReduction}%</div>
                            <p className="text-muted">Wait Time Reduction</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-info bg-opacity-10 border-info border-2 h-100">
                          <div className="card-body text-center">
                            <div className="h2 fw-bold text-info">{calculations.callAnswerRate}%</div>
                            <p className="text-muted">Call Answer Rate</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-info bg-opacity-10 border-info border-2 h-100">
                          <div className="card-body text-center">
                            <div className="h2 fw-bold text-info">{calculations.additionalPatientsServed.toLocaleString()}</div>
                            <p className="text-muted">More Patients Served/Month</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-info bg-opacity-10 border-info border-2 h-100">
                          <div className="card-body text-center">
                            <div className="h2 fw-bold text-info">+{calculations.patientSatisfactionGain}%</div>
                            <p className="text-muted">Patient Satisfaction</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-muted mt-3 mb-0">
                      <strong>How we calculate this:</strong> 24/7 availability, instant response times, and zero hold music mean
                      95% of calls are answered immediately. Recover 30% of previously missed calls and dramatically improve patient experience.
                    </p>
                  </div>

                  {/* Summary Total */}
                  <div className="card bg-light border-0 mt-4">
                    <div className="card-body text-center p-4">
                      <h4 className="mb-3 fw-bold">Total Financial Impact</h4>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="mb-2">
                            <span className="text-muted">Gross Monthly Benefit:</span>
                            <div className="h4 text-success">
                              ${(parseInt(calculations.monthlyRevenueIncrease) + parseInt(calculations.monthlyLaborCostSavings)).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <span className="text-muted">Gross Annual Benefit:</span>
                            <div className="h4 text-success">
                              ${(parseInt(calculations.annualRevenueIncrease) + parseInt(calculations.annualLaborCostSavings)).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="mb-2">
                            <span className="text-muted">Ankr Monthly Cost:</span>
                            <div className="h4 text-warning">
                              -${calculations.totalMonthlyCost.toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <span className="text-muted">Ankr Annual Cost:</span>
                            <div className="h4 text-warning">
                              -${calculations.totalAnnualCost.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr />
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-2">
                            <span className="text-muted fw-bold">Net Monthly Impact:</span>
                            <div className="h2 fw-bold text-success">
                              ${parseInt(calculations.netMonthlySavings).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <span className="text-muted fw-bold">Net Annual Impact:</span>
                            <div className="h2 fw-bold text-success">
                              ${parseInt(calculations.netAnnualSavings).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-muted mb-0 mt-3">
                        This represents your net financial benefit after accounting for Ankr&apos;s AI workforce costs
                      </p>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <div className="text-center mt-4">
                    <button
                      className="btn btn-success btn-lg px-5"
                      onClick={() => window.print()}
                    >
                      📄 Print This Report
                    </button>
                    <p className="text-muted mt-3 mb-0">
                      Ready to get started? Contact our team to schedule your implementation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROICalculator;
